import React from "react";
import Charts from "../components/Charts/Charts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faPlus,
  faSquarePlus,
} from "@fortawesome/free-solid-svg-icons";
import { GlobalContext } from "../context/GlobalContext";
import formatNumber from "../helpers/formatNumber";
import { calculateCashflow } from "../api";
import AddParameterModal from "../components/Cashflow/AddParameterModal";
import AddGroupModal from "../components/Cashflow/AddGroupModal";
import SEO from "../components/SEO/SEO";
import ToolDisclaimer from "../components/Disclaimer/ToolDisclaimer";

const InputGroup = ({
  title,
  input,
  setModalVisible,
  deleteParameter,
  setCurrentCategory,
}) => {
  const category = input?.filter((el) => el.categoryTitle === title)?.[0]
    ?.category;
  const categoryTitle = input?.filter((el) => el.categoryTitle === title)?.[0]
    ?.categoryTitle;
  const groupParameter = input?.filter((el) => el.categoryTitle === title);
  return (
    <>
      <h5>
        {title}
        <button
          className="btn btn-link dismiss-active-or-focus delete-form-label"
          data-toggle="modal"
          data-target="#addParameterModal"
          onClick={(e) => {
            e.preventDefault();
            setCurrentCategory({
              category: category,
              categoryTitle: categoryTitle,
            });
            setModalVisible((prev) => !prev);
          }}
        >
          <FontAwesomeIcon icon={faSquarePlus} />
        </button>
      </h5>
      {groupParameter?.map((obj) => {
        return (
          <div className="form-group">
            <label className="text-light" for="formName">
              {obj?.id}
              <button
                className="btn btn-link dismiss-active-or-focus delete-form-label"
                onClick={() => deleteParameter(obj?.id)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </label>
            <div className="input-group">
              <input
                type="number"
                className="form-control"
                id="formName"
                required
                step="any"
                defaultValue={obj?.default || 0}
                min={0}
              />
              <span className="input-group-text" id="basic-addon2">
                €
              </span>
            </div>
          </div>
        );
      })}
    </>
  );
};

const CashflowDescription = ({ income, expense }) => {
  if (income > expense)
    return (
      <>
        Damit bleiben dir
        <b className="visualizeLink linkIsInformative">{` ${formatNumber(
          (income - expense).toFixed(2)
        )}€ `}</b>
        übrig.
      </>
    );

  if (income < expense)
    return (
      <p>
        Somit übersteigen deine Ausgaben dein Budget um
        <b className="visualizeLink linkIsInformative">{` ${formatNumber(
          (expense - income).toFixed(2)
        )}€`}</b>
        .
      </p>
    );

  return <p>Deine Ausgaben werden von deinem Budget gedeckt.</p>;
};

const defaultParameter = [
  {
    id: "Gehalt",
    category: "income",
    categoryTitle: "Einkommen",
    from: "income",
    to: "budget",
    value: null,
    default: 1800,
  },
  {
    id: "Kaltmiete",
    category: "Miete",
    categoryTitle: "Miete",
    from: "budget",
    to: "expense",
    value: null,
    default: 750,
  },
  {
    id: "Nebenkosten",
    category: "Miete",
    categoryTitle: "Miete",
    from: "budget",
    to: "expense",
    value: null,
    default: 150,
  },
  {
    id: "ETF-Sparplan",
    category: "Investieren",
    categoryTitle: "Investieren",
    from: "budget",
    to: "expense",
    value: null,
    default: 200,
  },
  {
    id: "Leasing",
    category: "Auto",
    categoryTitle: "Auto",
    from: "budget",
    to: "expense",
    value: null,
    default: 300,
  },
];

function Cashflow() {
  const { isMobile } = React.useContext(GlobalContext);

  const [cashflowParameter, setCashflowParameter] =
    React.useState(defaultParameter);
  const [modalParameterVisible, setModalParameterVisible] =
    React.useState(false);
  const [modalGroupVisible, setModalGroupVisible] = React.useState(false);
  const [sankeyChartData, setSankeyChartData] = React.useState();
  const [totalAmount, setTotalAmount] = React.useState();
  const [currentCategory, setCurrentCategory] = React.useState();

  //* functions
  //* delete parameter
  const deleteParameter = (key) => {
    const updatedParameter = cashflowParameter.filter(
      (item) => item.id !== key
    );
    setCashflowParameter(updatedParameter);
  };

  //* update parameter
  const updateArray = (array1, array2) => {
    return array1.map((obj1) => {
      const match = array2.find((obj2) => obj2.label === obj1.id);

      if (match && match.value !== undefined) {
        return { ...obj1, value: match.value };
      }

      return obj1;
    });
  };

  //* create tooltip
  const createTooltip = (name, value) => {
    return `${name}: ${formatNumber(value)}€`;
  };

  //* summarize all values by category
  const sumCategory = (data) => {
    return data.reduce((acc, el) => {
      if (!acc[el.categoryTitle]) {
        acc[el.categoryTitle] = 0;
      }
      acc[el.categoryTitle] += el.value;
      return acc;
    }, {});
  };

  //* scroll to calculation
  const scrollToBottom = () => {
    const submitButton = document.getElementById("calculationSubmit");

    if (submitButton) {
      submitButton.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  //* handle form submit function
  const handleCalculationFormSubmit = async (e) => {
    e.preventDefault();

    const formGroups = document.querySelectorAll(".form-group");
    const formData = [];

    formGroups.forEach((group) => {
      const label = group.querySelector("label").textContent.trim();
      const input = group.querySelector("input");
      const value = input?.value;

      formData.push({ label, value });
    });

    // Output the extracted data
    const updatedArray = await updateArray(cashflowParameter, formData);

    try {
      const result = await calculateCashflow(JSON.stringify(updatedArray));
      const chartData = [
        ["From", "To", "Summe", { type: "string", role: "tooltip" }],
      ];

      const allExpenses = result?.data?.filter(
        (el) => el?.category !== "income"
      );
      const categoryData = sumCategory(allExpenses);

      Object.entries(categoryData)?.forEach((el) => {
        const newDataRow = [
          "Budget",
          el[0],
          el[1],
          createTooltip(el[0], el[1]),
        ];
        chartData.push(newDataRow);
      });

      result?.data?.forEach((el) => {
        if (el?.category === "income") {
          const newDataRow = [
            el?.id,
            "Budget",
            el?.value,
            createTooltip(el?.id, el?.value),
          ];
          chartData.push(newDataRow);
        } else {
          const newDataRow = [
            el?.categoryTitle,
            el?.id,
            el?.value,
            createTooltip(el?.id, el?.value),
          ];
          chartData.push(newDataRow);
        }
      });

      setSankeyChartData(chartData);
      setTotalAmount({
        income: result?.totalIncome,
        expense: result?.totalExpense,
      });
      scrollToBottom();
    } catch (error) {}
  };

  //* handle key down "enter"
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleCalculationFormSubmit(e);
      scrollToBottom();
    }
  };

  //* use Memos
  const formInputGroups = React.useMemo(() => {
    return [...new Set(cashflowParameter?.map((el) => el.categoryTitle))];
  }, [cashflowParameter]);

  return (
    <>
      <SEO
        title="Cashflow"
        keywords="Cashflow"
        description="Cashflow"
        type="Cashflow"
        url={window.location.href}
      />
      <AddParameterModal
        visible={modalParameterVisible}
        setVisible={setModalParameterVisible}
        setParameter={setCashflowParameter}
        currentCategory={currentCategory}
      />
      <AddGroupModal
        visible={modalGroupVisible}
        setVisible={setModalGroupVisible}
        setParameter={setCashflowParameter}
      />
      <div
        className={`${!isMobile && "article-text"} ${
          isMobile && "add-padding-20"
        } text-justify text-light`}
      >
        <h1 className="text-light article-header">Cashflow</h1>
        <ToolDisclaimer />

        <form
          onSubmit={handleCalculationFormSubmit}
          onKeyDown={handleKeyDown}
          id="compoundInterestForm"
        >
          {formInputGroups?.map((el) => {
            return (
              <InputGroup
                title={el}
                input={cashflowParameter}
                setModalVisible={setModalParameterVisible}
                deleteParameter={deleteParameter}
                setCurrentCategory={setCurrentCategory}
              />
            );
          })}

          <div className="text-right">
            <button
              className="btn btn-outline-secondary dismiss-active-or-focus add-form-label"
              data-toggle="modal"
              data-target="#addGroup"
              onClick={(e) => {
                e.preventDefault();
                setModalGroupVisible((prev) => !prev);
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
            <button
              type="submit"
              id="calculationSubmit"
              className="btn btn-primary formsubmit"
            >
              Berechnen
            </button>
          </div>
        </form>
        {sankeyChartData && totalAmount && (
          <>
            <br />
            <p>
              Du hast ein Budget von
              <b className="visualizeLink linkIsInformative">{` ${formatNumber(
                totalAmount?.income.toFixed(2)
              )}€ `}</b>
              um deine Gesamtausgaben belaufen sich auf
              <b className="visualizeLink linkIsInformative">
                {` ${formatNumber(totalAmount?.expense?.toFixed(2))}€`}
              </b>
              .&nbsp;
              <CashflowDescription
                income={totalAmount?.income}
                expense={totalAmount?.expense}
              />
            </p>

            <br />
            <Charts.Sankey data={sankeyChartData} />
          </>
        )}
      </div>
    </>
  );
}

export default Cashflow;
